// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {ReminderEntry} from '@app/features/messaging/state/Reminders';
import Reminders from '@app/features/messaging/state/Reminders';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {getRelativeDateString} from '@app/features/user/utils/DateFormatting';
import {APIErrorCodes} from '@fluxer/constants/src/ApiErrorCodes';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const logger = new Logger('Reminders');

function resolveClientTimeZone(): string | undefined {
	try {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return typeof timeZone === 'string' && timeZone.length > 0 ? timeZone : undefined;
	} catch {
		return undefined;
	}
}

const REMINDER_SET_DESCRIPTOR = msg({
	message: 'Reminder set for {when}',
	comment: 'Confirmation toast after scheduling a reminder. when is the formatted local time.',
});
const REMINDER_LIMIT_DESCRIPTOR = msg({
	message: "You've reached the reminder limit.",
	comment: 'Failure toast when scheduling a reminder is blocked by the pending reminder cap.',
});
const REMINDER_CLEARED_DESCRIPTOR = msg({
	message: 'Reminder cleared',
	comment: 'Confirmation toast after cancelling a pending message reminder.',
});

async function requestReminders(): Promise<Array<ReminderEntry>> {
	const response = await http.get<Array<ReminderEntry>>(Endpoints.USER_REMINDERS);
	return response.body ?? [];
}

export async function fetch(): Promise<void> {
	const generation = Reminders.snapshotFetchGeneration();
	try {
		Reminders.fetchSuccess(generation, await requestReminders());
	} catch (error) {
		Reminders.fetchError(generation);
		logger.error('Failed to fetch reminders:', error);
	}
}

export async function create(
	i18n: I18n,
	params: {channelId: string; messageId: string; remindAt: Date},
): Promise<void> {
	const {channelId, messageId, remindAt} = params;
	try {
		await http.post(Endpoints.CHANNEL_MESSAGE_REMIND(channelId, messageId), {
			body: {
				remind_at: remindAt.toISOString(),
				timezone: resolveClientTimeZone(),
			},
		});
		Reminders.add(messageId, remindAt);
		const whenLabel = getRelativeDateString(remindAt, i18n);
		ToastCommands.createToast({
			type: 'success',
			timeout: 8000,
			children: i18n._(REMINDER_SET_DESCRIPTOR, {when: whenLabel}).replaceAll('{when}', whenLabel),
		});
	} catch (error) {
		logger.error(`Failed to schedule reminder for message ${messageId}:`, error);
		if (failureCode(error) === APIErrorCodes.MAX_REMINDERS) {
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(REMINDER_LIMIT_DESCRIPTOR),
			});
			return;
		}
		throw error;
	}
}

export async function clear(i18n: I18n, params: {channelId: string; messageId: string}): Promise<void> {
	const {channelId, messageId} = params;
	try {
		await http.delete(Endpoints.CHANNEL_MESSAGE_REMIND(channelId, messageId));
		Reminders.remove(messageId);
		ToastCommands.createToast({
			type: 'success',
			children: i18n._(REMINDER_CLEARED_DESCRIPTOR),
		});
	} catch (error) {
		logger.error(`Failed to clear reminder for message ${messageId}:`, error);
		throw error;
	}
}
